
import BaseHttpClient from '@/core/HttpResources/BaseHttpClient';
import Documento from '@/models/documento.model';
import { UPLOAD_HTTP_TIMEOUT } from '@/core/HttpResources/BaseApi';
import axios from 'axios';
import { getSessionToken } from '@/helpers/sessionStorage';

export type DocumentDownloadErrorKind =
    'missing-token' |
    'popup-blocked' |
    'unauthorized' |
    'forbidden' |
    'not-found' |
    'request-failed';

export class DocumentDownloadError extends Error {
    constructor(public kind: DocumentDownloadErrorKind) {
        super(kind);
        Object.setPrototypeOf(this, DocumentDownloadError.prototype);
    }
}

export function getDocumentDownloadErrorMessage(error: any): string {
    if (!(error instanceof DocumentDownloadError)) {
        return 'No se pudo abrir el documento.';
    }

    switch (error.kind) {
        case 'missing-token':
        case 'unauthorized':
            return 'Su sesión ha expirado. Inicie sesión nuevamente.';
        case 'forbidden':
            return 'No tiene permisos para acceder a este documento.';
        case 'not-found':
            return 'El documento solicitado no está disponible.';
        case 'popup-blocked':
            return 'Habilite las ventanas emergentes para visualizar el documento.';
        default:
            return 'No se pudo abrir el documento.';
    }
}
class FileService {
    private readonly blobUrlLifetimeMilliseconds = 12 * 60 * 60 * 1000;
    private readonly closedWindowCheckMilliseconds = 5000;
    private client: BaseHttpClient;
    constructor(public controller: string = 'files') {
        this.client = new BaseHttpClient(controller);
    }

    public async uploadMany(files: File[], action = 'upload-many'): Promise<Documento[]> {
        try {
            var data = new FormData();
            files.forEach((x) => data.append('files', x));
            var result = await this.client.postAsync<Documento[]>(action, data, Object(), { timeoutMs: UPLOAD_HTTP_TIMEOUT });
            return result;
        } catch (error) {
            throw error;
        }
    }
    public async upload(file: File, action = 'upload'): Promise<Documento> {
        try {
            var data = new FormData();
            data.append('file', file);
            var result = await this.client.postAsync<Documento>(action, data, Object(), { timeoutMs: UPLOAD_HTTP_TIMEOUT });
            return result;
        } catch (error) {
            throw error;
        }
    }

    public async download(documentoId: number): Promise<void> {
        await this.openAuthenticatedPdf(`files/content?documentoId=${encodeURIComponent(documentoId.toString())}`);
    }

    public async openAuthenticatedPdf(path: string): Promise<void> {
        const authorization = this.getAuthorizationHeader();

        const documentWindow = window.open('about:blank', '_blank');
        if (!documentWindow) {
            throw new DocumentDownloadError('popup-blocked');
        }

        documentWindow.opener = null;

        let objectUrl: string | undefined;
        let cleanupScheduled = false;

        try {
            const response = await axios.get<Blob>(
                this.getApiUrl(path),
                {
                    responseType: 'blob',
                    headers: { Authorization: authorization }
                }
            );

            if (documentWindow.closed) {
                return;
            }

            objectUrl = URL.createObjectURL(response.data);
            documentWindow.location.replace(objectUrl);
            this.scheduleObjectUrlCleanup(documentWindow, objectUrl);
            cleanupScheduled = true;
        } catch (error) {
            if (objectUrl && !cleanupScheduled) {
                URL.revokeObjectURL(objectUrl);
            }
            documentWindow.close();

            const status = error && error.response ? error.response.status : undefined;
            if (status === 401) {
                throw new DocumentDownloadError('unauthorized');
            }
            if (status === 403) {
                throw new DocumentDownloadError('forbidden');
            }
            if (status === 404) {
                throw new DocumentDownloadError('not-found');
            }

            throw new DocumentDownloadError('request-failed');
        }
    }

    private getAuthorizationHeader(): string {
        const token = getSessionToken();
        if (!token) {
            throw new DocumentDownloadError('missing-token');
        }

        return token.startsWith('Bearer ') ? token : `Bearer ${token}`;
    }

    private getApiUrl(path: string): string {
        return `${process.env.VUE_APP_API_URL}${path.replace(/^\/+/, '')}`;
    }

    private scheduleObjectUrlCleanup(documentWindow: Window, objectUrl: string): void {
        let cleanedUp = false;
        let closeMonitor: number | undefined;
        let expirationTimer: number | undefined;

        const cleanup = () => {
            if (cleanedUp) {
                return;
            }

            cleanedUp = true;
            if (closeMonitor !== undefined) {
                window.clearInterval(closeMonitor);
            }
            if (expirationTimer !== undefined) {
                window.clearTimeout(expirationTimer);
            }
            URL.revokeObjectURL(objectUrl);
        };

        closeMonitor = window.setInterval(() => {
            if (documentWindow.closed) {
                cleanup();
            }
        }, this.closedWindowCheckMilliseconds);

        expirationTimer = window.setTimeout(cleanup, this.blobUrlLifetimeMilliseconds);
    }
}

export default new FileService();
